import React, { useState } from "react";
import Button from "../../atoms/Button/Button";
import { Check, Delete } from "@mui/icons-material";
import LogoutIcon from "@mui/icons-material/Logout";
import RestoreIcon from "@mui/icons-material/Restore";
import { Avatar } from "@mui/material";
import {
  getUserInitials,
  getUserTypeDisplayName,
} from "../../../services/helpers/commonHelpers";
import { setUserName, UserType } from "../../../redux/reducers/settingsReducer";
import ConfirmDialog from "../../atoms/ConfirmDialog/ConfirmDialog";
import Card from "../Card/Card";
import { revertUser, updateUser } from "../../../services/api/user.api";
import { useDispatch } from "react-redux";
import { setIsFullScreenLoading } from "../../../redux/reducers/loadingReducer";
import TextField from "../../atoms/TextField/TextField";
import { useTranslation } from "react-i18next";

interface SettingsProfileCardProps {
  user: UserType | undefined;
  onLogout: (deleteUserData?: boolean) => void;
}

const SettingsProfileCard = (props: SettingsProfileCardProps) => {
  const { user, onLogout } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isDeleteAccountOpen, setIsDeleteAccountOpen] = useState(false);
  const [isRevertUserOpen, setIsRevertUserOpen] = useState(false);
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [textValue, setTextValue] = useState(user?.name ?? "");

  const handleResetTemplate = () => {
    setIsRevertUserOpen(false);
    dispatch(setIsFullScreenLoading(true));
    revertUser(dispatch).then((response) => {
      dispatch(setIsFullScreenLoading(false));
      window.location.reload();
    });
  };

  const handleSaveName = () => {
    setIsInEditMode(false);
    dispatch(setIsFullScreenLoading(true));
    updateUser(dispatch, textValue).then((response) => {
      dispatch(setIsFullScreenLoading(false));
      dispatch(setUserName(textValue));
    });
  };

  return (
    <Card
      title={t("profile")}
      editable
      onEdit={() => setIsInEditMode(!isInEditMode)}
    >
      <div className="dui-settings-profile">
        <div>
          <Avatar>{getUserInitials(user?.name ?? "")}</Avatar>
        </div>
        <div className="dui-settings-profile-content">
          <div className="dui-settings-profile-content-item">
            <b>{t("name")}:</b>
            {isInEditMode ? (
              <div className="dui-settings-profile-content-edit-item">
                <TextField
                  value={textValue}
                  onChange={setTextValue}
                  onEnter={handleSaveName}
                />
                <Button
                  type="text"
                  corners="rounded"
                  icon={<Check />}
                  className="dui-btn-save"
                  onClick={handleSaveName}
                />
              </div>
            ) : (
              user?.name
            )}
          </div>
          <div className="dui-settings-profile-content-item">
            <b>{t("email")}:</b> {user?.email}
          </div>
          <div className="dui-settings-profile-content-item">
            <b>{t("type")}:</b> {getUserTypeDisplayName(user?.type ?? "", t)}
          </div>
        </div>
      </div>
      <div className="dui-settings-card-buttons">
        <Button onClick={() => onLogout()} icon={<LogoutIcon />}>
          {t("logout")}
        </Button>
        <Button
          onClick={() => setIsRevertUserOpen(true)}
          icon={<RestoreIcon />}
        >
          {t("revertUser")}
        </Button>
        <Button
          icon={<Delete />}
          appearance="error"
          onClick={() => setIsDeleteAccountOpen(true)}
        >
          {t("deleteAccount")}
        </Button>
      </div>
      <ConfirmDialog
        open={isDeleteAccountOpen}
        onClose={() => setIsDeleteAccountOpen(false)}
        onConfirm={() => onLogout(true)}
        title={t("deleteAccountConfirmationTitle")}
        description={t("deleteAccountConfirmation")}
        confirmText={t("deleteAccountConfirmText")}
        cancelText={t("cancel")}
        confirmButtonAppearance="error"
      />
      <ConfirmDialog
        open={isRevertUserOpen}
        onClose={() => setIsRevertUserOpen(false)}
        onConfirm={() => handleResetTemplate()}
        title={t("revertUser")}
        description={t("revertUserConfirmation")}
        confirmText={t("revertUserConfirmText")}
        cancelText={t("cancel")}
        confirmButtonAppearance="error"
      />
    </Card>
  );
};

export default SettingsProfileCard;
